export interface NavigationItem {
	id: string;
	title: string;
	type: 'item' | 'collapse' | 'group';
	translate?: string;
	icon?: string;
	hidden?: boolean;
	url?: string;
	classes?: string;
	groupClasses?: string;
	exactMatch?: boolean;
	external?: boolean;
	target?: boolean;
	breadcrumbs?: boolean;
	children?: NavigationItem[];
	link?: string;
	description?: string;
	path?: string;
}

/**
 * Obtiene el rol del usuario desde localStorage
 * @returns El rol del usuario o null si no está disponible
 */
function getUserRole(): number | null {
	try {
		const userInfo = localStorage.getItem('userInfo');
		if (userInfo) {
			const parsed = JSON.parse(userInfo);
			return parsed.rol || null;
		}
	} catch (error) {
		console.error('Error al obtener el rol del usuario:', error);
	}
	return null;
}

/**
 * Filtra los módulos de navegación según el rol del usuario
 * - Rol 1 o 2: Acceso completo a todos los módulos
 * - Rol 5: Solo acceso a Dashboard y Gestión de servicios
 * @param items Array de items de navegación
 * @param userRole Rol del usuario
 * @returns Array filtrado de items de navegación
 */
function filterNavigationByRole(items: NavigationItem[], userRole: number | null): NavigationItem[] {
	// Si no hay rol o es rol 1 o 2, mostrar todo excepto gestion_beneficiaries
	if (!userRole || userRole === 1 || userRole === 2) {
		return items.map((group) => {
			if (group.children) {
				return {
					...group,
					children: group.children.filter((child) => child.id !== 'gestion_beneficiaries')
				};
			}
			return group;
		});
	} else if (userRole === 5) {
		return items
			.map((group) => {
				const filteredGroup = { ...group };
				if (group.id === 'dashboard' && group.children) {
					filteredGroup.children = group.children.filter((child) => child.id === 'default');
					return filteredGroup.children.length > 0 ? filteredGroup : null;
				}
				if (group.id === 'driver' && group.children) {
					// Filtrar solo "Gestión de servicios"
					filteredGroup.children = group.children.filter(
						(child) => child.id === 'gestion_servicios' || child.id === 'gestion_beneficiaries'
					);
					// Solo retornar el grupo si tiene hijos
					return filteredGroup.children.length > 0 ? filteredGroup : null;
				}
				if (group.id === 'config' && group.children) {
					filteredGroup.children = group.children.filter((child) => child.id === 'gestion_lineas_atencion');
					return filteredGroup.children.length > 0 ? filteredGroup : null;
				}

				// No mostrar otros grupos
				return null;
			})
			.filter((item): item is NavigationItem => item !== null);
	}

	// Por defecto, no mostrar nada si el rol no está definido
	return [];
}

export const NavigationItems: NavigationItem[] = [
	{
		id: 'dashboard',
		title: 'Dashboard',
		type: 'group',
		icon: 'bi bi-speedometer2',
		children: [
			{
				id: 'default',
				title: 'Home',
				type: 'item',
				classes: 'nav-item',
				url: '/dashboard/home',
				icon: 'bi bi-house-door-fill',
				breadcrumbs: false
			},
			{
				id: 'mapa_conductores',
				title: 'Mapa de Conductores',
				type: 'item',
				classes: 'nav-item',
				url: '/drivers/map',
				icon: 'bi bi-map-fill',
				breadcrumbs: false
			}
		]
	},
	{
		id: 'driver',
		title: 'Gestión',
		type: 'group',
		icon: 'bi bi-gear-fill',
		children: [
			{
				id: 'gestion_servicios',
				title: 'Gestión de servicios',
				type: 'item',
				classes: 'nav-item',
				url: '/services/list',
				icon: 'bi bi-diagram-3',
				breadcrumbs: false
			},
			{
				id: 'gestion_liquidacion_viajes',
				title: 'Liquidación de viajes',
				type: 'item',
				classes: 'nav-item',
				url: '/driver/settlement',
				icon: 'bi bi-cash-coin',
				breadcrumbs: false
			},
			{
				id: 'gestion_conductores',
				title: 'Gestión de conductores',
				type: 'item',
				classes: 'nav-item',
				url: '/driver/list',
				icon: 'bi bi-people-fill',
				breadcrumbs: false
			},

			{
				id: 'gestion_pasajeros',
				title: 'Gestión de pasajeros',
				type: 'item',
				classes: 'nav-item',
				url: '/passenger/list',
				icon: 'bi bi-person-lines-fill',
				breadcrumbs: false
			},
			{
				id: 'gestion_vehiculos',
				title: 'Gestión de vehículos',
				type: 'item',
				classes: 'nav-item',
				url: '/vehicle/list',
				icon: 'bi bi-car-front',
				breadcrumbs: false
			},
			{
				id: 'gestion_cupones',
				title: 'Gestión de cupones',
				type: 'item',
				classes: 'nav-item',
				url: '/coupon/list',
				icon: 'bi bi-coin',
				breadcrumbs: false
			},
			{
				id: 'gestion_tarifas_locales',
				title: 'Gestión de tarifas locales',
				type: 'item',
				classes: 'nav-item',
				url: '/rates/local',
				icon: 'bi bi-pin-map',
				breadcrumbs: false
			},
			{
				id: 'gestion_tarifas_foraneas',
				title: 'Gestión de tarifas foráneas',
				type: 'item',
				classes: 'nav-item',
				url: '/rates/foreigner',
				icon: 'bi bi-truck',
				breadcrumbs: false
			},
			{
				id: 'gestion_inspecciones',
				title: 'Gestión de inspecciones',
				type: 'item',
				classes: 'nav-item',
				url: '/pre-operational/list',
				icon: 'bi bi-cone-striped',
				breadcrumbs: false
			},
			{
				id: 'gestion_notificaciones_push',
				title: 'Gestión notificaciones push',
				type: 'item',
				classes: 'nav-item',
				url: '/push-notifications/send',
				icon: 'bi bi-bell-fill',
				breadcrumbs: false
			},
			{
				id: 'gestion_beneficiaries',
				title: 'Gestión de beneficiarios',
				type: 'item',
				classes: 'nav-item',
				url: '/enterprise/beneficiaries/list',
				icon: 'bi bi-people-fill',
				breadcrumbs: false
			}
		]
	},
	{
		id: 'config',
		title: 'Configuración',
		type: 'group',
		icon: 'bi bi-gear',
		children: [
			{
				id: 'gestion_marcas',
				title: 'Marcas',
				type: 'item',
				classes: 'nav-item',
				url: '/brands/list',
				icon: 'bi bi-tags-fill',
				breadcrumbs: false
			},
			{
				id: 'gestion_categorias',
				title: 'Categoría vehiculo',
				type: 'item',
				classes: 'nav-item',
				url: '/category/list',
				icon: 'bi bi-car-front-fill',
				breadcrumbs: false
			},
			{
				id: 'gestion_administradores',
				title: 'Admin',
				type: 'item',
				classes: 'nav-item',
				url: '/admin/list',
				icon: 'bi bi-person-badge-fill',
				breadcrumbs: false
			},
			{
				id: 'gestion_empresas',
				title: 'Empresas',
				type: 'item',
				classes: 'nav-item',
				url: '/enterprise/list',
				icon: 'bi bi-building-fill',
				breadcrumbs: false
			},
			{
				id: 'gestion_lineas_atencion',
				title: 'Linea de atención',
				type: 'item',
				classes: 'nav-item',
				url: '/helplines/list',
				icon: 'bi bi-telephone-fill',
				breadcrumbs: false
			},
			{
				id: 'gestion_fuec',
				title: 'FUEC',
				type: 'item',
				classes: 'nav-item',
				url: '/fuec-config',
				icon: 'bi bi-file-text-fill',
				breadcrumbs: false
			},
			{
				id: 'gestion_app_versions',
				title: 'Versiones de la aplicación',
				type: 'item',
				classes: 'nav-item',
				url: '/app-versions/list',
				icon: 'bi bi-phone',
				breadcrumbs: false
			},
			{
				id: 'gestion_promociones',
				title: 'Promociones',
				type: 'item',
				classes: 'nav-item',
				url: '/promotions/list',
				icon: 'bi bi-megaphone-fill',
				breadcrumbs: false
			}
			/*{
				id: 'flags',
				title: 'Flags',
				type: 'item',
				classes: 'nav-item',
				url: '/flags/list',
				icon: 'bi bi-flag-fill',
				breadcrumbs: false
			}*/
		]
	}
];

/**
 * Obtiene los items de navegación filtrados según el rol del usuario actual
 * @returns Array de items de navegación filtrados según el rol
 */
export function getFilteredNavigationItems(): NavigationItem[] {
	const userRole = getUserRole();
	return filterNavigationByRole(NavigationItems, userRole);
}
